import React, { useState } from "react";
import { Formik, Form, Field, FieldArray } from "formik";
import * as Yup from "yup";
import TinyMCEEditor from "./TinyMCEEditor";
import { useDispatch } from "react-redux";
import { uploadExcel } from "utils/quizThunk";
import toast from "react-hot-toast";

const QUESTION_TYPES = [
  { label: "Single Selection", value: "single" },
  { label: "True/False", value: "truefalse" },
  { label: "Multiple Choice", value: "multiple" },
];

const QUESTION_LEVELS = [
  { label: "Easy", value: "easy" },
  { label: "Medium", value: "medium" },
  { label: "Hard", value: "hard" },
];

const MEDIUM = [
  { label: "Hindi", value: "hindi" },
  { label: "English", value: "english" },
];

const validationSchema = Yup.object({
  description: Yup.string().required("Question description is required"),
  questionType: Yup.string().required("Question type is required"),
  medium: Yup.string().required("Medium is required"),
  questionLevel: Yup.string().required("Question level is required"),
  answers: Yup.array()
    .of(
      Yup.object({
        text: Yup.string().required("Option text is required"),
        isCorrect: Yup.boolean().required(),
      })
    )
    .min(1, "At least one answer is required"),
  correctAnswerIndex: Yup.number().when("questionType", {
    is: (value) => ["single", "truefalse"].includes(value),
    then: (schema) =>
      schema
        .integer()
        .min(0, "Select a correct answer")
        .required("Select a correct answer"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const initialValues = {
  description: "",
  medium: "",
  questionType: "",
  questionLevel: "",
  answers: [],
  correctAnswerIndex: -1,
};

// Custom component to integrate TinyMCE with Formik
const TinyMCEField = ({ field, form, ...props }) => {
  const { name, value } = field;
  const { setFieldValue, setFieldTouched } = form;

  return (
    <TinyMCEEditor
      value={value}
      onChange={(content) => {
        console.log("TinyMCE content changed:", content); // Debug
        setFieldValue(name, content);
        setFieldTouched(name, true);
      }}
      {...props}
    />
  );
};

const AddQuestionModal = ({ handleSubmit,handleExcelUpload,setFile, closeModal, type }) => {
  console.log(type);
  const [activeTab, setActiveTab] = useState(type);
  
  const dispatch = useDispatch();
  const handleTypeChange = (setFieldValue, value) => {
    console.log("Question type changed to:", value); // Debug
    setFieldValue("questionType", value);
    if (value === "truefalse") {
      setFieldValue("answers", [
        { text: "True", isCorrect: false },
        { text: "False", isCorrect: false },
      ]);
      setFieldValue("correctAnswerIndex", -1);
    } else if (value === "single") {
      setFieldValue(
        "answers",
        Array(4)
          .fill()
          .map(() => ({ text: "", isCorrect: false }))
      );
      setFieldValue("correctAnswerIndex", -1);
    } else {
      setFieldValue("answers", []);
      setFieldValue("correctAnswerIndex", -1);
    }
  };

  const handleMediumChange = (setFieldValue, value) => {
    console.log("Medium changed to:", value); // Debug
    setFieldValue("medium", value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      console.log("Enter key prevented from submitting form"); // Debug
    }
  };
  

  return (
    <div className="modal fade" id="add_question">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="fw-bold">Add New Question</h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              onClick={closeModal}
              aria-label="Close modal"
            />
          </div>
          <div className="modal-body">
            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button
                  className={`nav-link ${type === "manual" ? "active" : ""}`}
                  onClick={() => setActiveTab("manual")}
                  aria-label="Manual Entry"
                >
                  Manual Entry
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${type === "excel" ? "active" : ""}`}
                  onClick={() => setActiveTab("excel")}
                  aria-label="Upload Excel"
                >
                  Upload Excel
                </button>
              </li>
            </ul>

            {type === "manual" && (
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                enableReinitialize
                validateOnChange
                validateOnBlur
                onSubmit={async (values, { resetForm, setTouched }) => {
                  try {
                    console.log("Form submitted with values:", values); // Debug
                    // Set all fields as touched to show errors
                    setTouched({
                      description: true,
                      questionType: true,
                      medium: true,
                      questionLevel: true,
                      answers: values.answers.map(() => ({
                        text: true,
                        isCorrect: true,
                      })),
                      correctAnswerIndex: true,
                    });

                    // Validate form manually
                    await validationSchema.validate(values, {
                      abortEarly: false,
                    });

                    // Update answers with correctAnswerIndex
                    const updatedAnswers = values.answers.map((ans, idx) => ({
                      ...ans,
                      isCorrect:
                        values.questionType === "single" ||
                        values.questionType === "truefalse"
                          ? idx === Number(values.correctAnswerIndex)
                          : ans.isCorrect,
                    }));

                    handleSubmit(
                      { ...values, answers: updatedAnswers },
                      { resetForm }
                    );
                    closeModal("add_question");
                    resetForm();
                  } catch (error) {
                    console.error("Validation failed:", error.errors); // Debug
                  }
                }}
              >
                {({
                  values,
                  setFieldValue,
                  touched,
                  errors,
                  isValid,
                  isSubmitting,
                }) => (
                  <Form onKeyDown={handleKeyDown}>
                    <div className="mb-3">
                      <label className="form-label" htmlFor="description">
                        Question Description
                      </label>
                      <Field
                        name="description"
                        component={TinyMCEField}
                        aria-label="Question description with rich text and images"
                      />
                      {touched.description && errors.description && (
                        <div className="text-danger">{errors.description}</div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label" htmlFor="medium">
                        Medium
                      </label>
                      <Field
                        as="select"
                        name="medium"
                        id="medium"
                        className="form-select"
                        onChange={(e) =>
                          handleMediumChange(setFieldValue, e.target.value)
                        }
                        aria-label="Select medium"
                      >
                        <option value="">Select Medium</option>
                        {MEDIUM.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Field>
                      {touched.medium && errors.medium && (
                        <div className="text-danger">{errors.medium}</div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label" htmlFor="questionType">
                        Question Type
                      </label>
                      <Field
                        as="select"
                        name="questionType"
                        id="questionType"
                        className="form-select"
                        onChange={(e) =>
                          handleTypeChange(setFieldValue, e.target.value)
                        }
                        aria-label="Select question type"
                      >
                        <option value="">Select Type</option>
                        {QUESTION_TYPES.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Field>
                      {touched.questionType && errors.questionType && (
                        <div className="text-danger">{errors.questionType}</div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label" htmlFor="questionLevel">
                        Question Level
                      </label>
                      <Field
                        as="select"
                        name="questionLevel"
                        id="questionLevel"
                        className="form-select"
                        aria-label="Select question level"
                      >
                        <option value="">Select Level</option>
                        {QUESTION_LEVELS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Field>
                      {touched.questionLevel && errors.questionLevel && (
                        <div className="text-danger">
                          {errors.questionLevel}
                        </div>
                      )}
                    </div>

                    <h6>Answers</h6>
                    <FieldArray name="answers">
                      {({ push, remove }) => (
                        <div>
                          {values.answers.map((_, idx) => (
                            <div
                              key={idx}
                              className="d-flex mb-2 align-items-center"
                            >
                              <Field
                                type="text"
                                name={`answers[${idx}].text`}
                                className="form-control me-2"
                                placeholder={`Option ${idx + 1}`}
                                aria-label={`Option ${idx + 1}`}
                              />
                              {values.questionType === "single" ||
                              values.questionType === "truefalse" ? (
                                <input
                                  type="radio"
                                  name="correctAnswerIndex"
                                  value={String(idx)}
                                  className="form-check-input"
                                  checked={
                                    Number(values.correctAnswerIndex) === idx
                                  }
                                  onChange={() =>
                                    setFieldValue("correctAnswerIndex", idx)
                                  }
                                  aria-label={`Set option ${
                                    idx + 1
                                  } as correct`}
                                />
                              ) : (
                                <Field
                                  type="checkbox"
                                  name={`answers[${idx}].isCorrect`}
                                  className="form-check-input"
                                  aria-label={`Mark option ${
                                    idx + 1
                                  } as correct`}
                                />
                              )}
                              {values.questionType === "multiple" && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger ms-2 rounded-pill"
                                  onClick={() => remove(idx)}
                                  aria-label={`Remove option ${idx + 1}`}
                                >
                                  <i className="isax isax-trash"></i>
                                </button>
                              )}
                              {touched.answers?.[idx]?.text &&
                                errors.answers?.[idx]?.text && (
                                  <div className="text-danger">
                                    {errors.answers[idx].text}
                                  </div>
                                )}
                            </div>
                          ))}
                          {touched.answers &&
                            errors.answers &&
                            typeof errors.answers === "string" && (
                              <div className="text-danger mb-2">
                                {errors.answers}
                              </div>
                            )}
                          {values.questionType === "multiple" && (
                            <button
                              type="button"
                              className="btn btn-sm btn-primary mt-2 rounded-pill"
                              onClick={() =>
                                push({ text: "", isCorrect: false })
                              }
                              aria-label="Add option"
                            >
                              Add Option
                            </button>
                          )}
                        </div>
                      )}
                    </FieldArray>
                    {touched.correctAnswerIndex &&
                      errors.correctAnswerIndex && (
                        <div className="text-danger mb-3">
                          {errors.correctAnswerIndex}
                        </div>
                      )}

                    <div className="modal-footer mt-3">
                      <button
                        type="button"
                        className="btn bg-gray-100 rounded-pill"
                        data-bs-dismiss="modal"
                        onClick={closeModal}
                        aria-label="Cancel"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-secondary rounded-pill"
                        disabled={isSubmitting || !isValid}
                        aria-label="Add question"
                      >
                        Add Question
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            )}

            {type === "excel" && (
              <div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="excelUpload">
                    Upload Excel File
                  </label>
                  <input
                    type="file"
                    id="excelUpload"
                    className="form-control"
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      if (e.target.files.length > 0) {
                        console.log(
                          "Excel file selected:",
                          e.target.files[0].name
                        ); // Debug
                        setFile(e.target.files[0]);
                      }
                    }}
                    aria-label="Upload Excel file"
                  />
                </div>
                <div className="modal-footer mt-3">
                  <button
                    type="button"
                    className="btn btn-secondary rounded-pill"
                    onClick={() =>
                      window.open("/sample_quiz_questions.xlsx", "_blank")
                    }
                  >
                    Download Sample
                  </button>
                  <button
                    type="button"
                    className="btn bg-gray-100 rounded-pill"
                    // data-bs-dismiss="modal"
                    onClick={handleExcelUpload}
                    // aria-label="Close"
                  >
                    Upload
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddQuestionModal;
